import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Arrow,
  Circle,
  Group,
  Image as KonvaImage,
  Line,
  Path,
  Rect,
  Text,
} from 'react-konva'

import type { FlattenedDocumentPage } from '@/lib/api/client'
import type { TemplateAttribute } from '@/services/hooks/template_attributes'
import type { KonvaEventObject } from 'konva/lib/Node'
import {
  getArrowTextLayout,
  getBubbleLayout,
  getLocalAnnotationVisualRect,
  getSelectionFrame,
  isLineBasedAnnotation,
  type SelectionFrame,
} from './annotationGeometry'
import {
  DEFAULT_KONVA_STROKE,
  type KonvaAnnotationTemplate,
} from './annotationSchema'
import { getAbsoluteYFromSubPage } from './utils'
import KonvaAnnotationBoundsShape from './KonvaAnnotationBoundsShape'

type KonvaAnnotationNodeProps = {
  annotation: KonvaAnnotationTemplate
  template: TemplateAttribute<any>
  documentPage: FlattenedDocumentPage
  draggable?: boolean
  dragOffset?: { x: number; y: number }
  isSelected?: boolean
  lineResizePoints?: number[] | null
  renderArrowText?: boolean
  onDragStart?: (position: { x: number; y: number }) => void
  onDragEnd?: (position: { x: number; y: number }) => void
  onDragMove?: (position: { x: number; y: number }) => void
  onPointerDown?: (event: MouseEvent | TouchEvent) => void
  onPointerUp?: (event: MouseEvent | TouchEvent) => void
  onSelect?: () => void
  onContextMenu?: (event: KonvaEventObject<MouseEvent>) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onNodeRef?: (templateId: string, node: any | null) => void
}

type HardwareIconImageProps = {
  src: string
  width: number
  boxHeight: number
}

type HardwareCheckmarkIconProps = {
  width: number
  height: number
  color: string
}

const DEFAULT_TEXT_FONT_FAMILY =
  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

const SelectionFrameContent = ({
  children,
  frame,
}: {
  children: ReactNode
  frame: SelectionFrame
}) => {
  if (frame.mode === 'axis') {
    return <>{children}</>
  }

  return (
    <Group x={frame.origin.x} y={frame.origin.y} rotation={frame.angle}>
      {children}
    </Group>
  )
}

const HardwareCheckmarkIcon = ({
  width,
  height,
  color,
}: HardwareCheckmarkIconProps) => {
  const scaleX = width / 21
  const iconHeight = width * (16 / 21)
  const scaleY = iconHeight / 16

  return (
    <Path
      data="M19.7577 0.890952C19.2531 0.396074 18.4455 0.395201 17.9398 0.888987L6.05631 12.4927C6.04525 12.5035 6.02782 12.5042 6.01592 12.4943L2.68278 9.72755C2.14071 9.27759 1.33871 9.34201 0.875407 9.87272C0.395504 10.4225 0.462772 11.2596 1.0243 11.7257L5.25657 15.2383C5.78479 15.672 6.55469 15.6388 7.04368 15.1614L19.7557 2.74868C20.2771 2.23953 20.278 1.40123 19.7577 0.890952Z"
      y={(height - iconHeight) / 2}
      scaleX={scaleX}
      scaleY={scaleY}
      fill={color}
    />
  )
}

const HardwareIconImage = ({
  src,
  width,
  boxHeight,
}: HardwareIconImageProps) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    const nextImage = new window.Image()
    nextImage.onload = () => setImage(nextImage)
    nextImage.src = src

    return () => {
      nextImage.onload = null
    }
  }, [src])

  if (!image) return null

  const inferredHeight =
    image.naturalWidth > 0
      ? width * (image.naturalHeight / image.naturalWidth)
      : boxHeight

  return (
    <KonvaImage
      image={image}
      y={(boxHeight - inferredHeight) / 2}
      width={width}
      height={inferredHeight}
    />
  )
}

const getTextPosition = ({
  annotation,
  width,
  height,
}: {
  annotation: KonvaAnnotationTemplate
  width: number
  height: number
}) => {
  if (!annotation.text?.enabled) return null

  if (annotation.geometry.mode === 'points') {
    const pointWidth = annotation.geometry.width ?? width
    const pointHeight = annotation.geometry.height ?? height
    const placement = annotation.text.placement ?? 'inside'

    if (placement === 'bottom') {
      return {
        x: Math.min(0, pointWidth) - 20,
        y: Math.max(0, pointHeight) + 8,
        width: Math.abs(pointWidth) + 40,
      }
    }

    return {
      x: Math.min(0, pointWidth) - 20,
      y: Math.min(0, pointHeight) - 24,
      width: Math.abs(pointWidth) + 40,
    }
  }

  return {
    x: annotation.text.padding ?? 8,
    y: height / 2 - (annotation.text.fontSize ?? 14) / 2,
    width: Math.max(width - 2 * (annotation.text.padding ?? 8), 24),
  }
}

const changeCursorType = (event: KonvaEventObject<Event>, type: string) => {
  const container = event.target.getStage()?.container()
  if (container) {
    container.style.cursor = type
  }
}

const markKonvaAnnotationPointerDown = () => {
  ;(
    window as Window & {
      __q20LastKonvaAnnotationPointerDownAt?: number
    }
  ).__q20LastKonvaAnnotationPointerDownAt = Date.now()
}

const KonvaAnnotationNodeComponent = ({
  annotation,
  template,
  documentPage,
  draggable = false,
  dragOffset,
  isSelected = false,
  lineResizePoints,
  renderArrowText = true,
  onDragStart,
  onDragEnd,
  onDragMove,
  onPointerDown,
  onPointerUp,
  onSelect,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  onNodeRef,
}: KonvaAnnotationNodeProps) => {
  const groupRef = useRef<any>(null)
  const subPageIds = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id as string,
      ],
    [documentPage],
  )

  const { x, y, rotation } = useMemo(() => {
    if (annotation.geometry.mode === 'bounds') {
      return {
        x: annotation.geometry.x,
        y: getAbsoluteYFromSubPage({
          subPageId: annotation.subPageId ?? (documentPage.id as string),
          relativeY: annotation.geometry.y,
          subPageIds,
        }),
        rotation: annotation.geometry.rotation ?? 0,
      }
    }

    return {
      x: annotation.geometry.x,
      y: getAbsoluteYFromSubPage({
        subPageId: annotation.subPageId ?? (documentPage.id as string),
        relativeY: annotation.geometry.y,
        subPageIds,
      }),
      rotation: annotation.geometry.rotation ?? 0,
    }
  }, [annotation, documentPage.id, subPageIds])

  const stroke = annotation.style.stroke ?? DEFAULT_KONVA_STROKE
  const fill = annotation.style.fill ?? undefined
  const strokeWidth = annotation.style.strokeWidth ?? 2
  const opacity = annotation.style.opacity ?? 1
  const isLegacySquare = annotation.legacyType === 'square'
  const isLegacyTriangle = annotation.legacyType === 'triangle'
  const displayAnnotation = useMemo<KonvaAnnotationTemplate>(() => {
    if (!lineResizePoints || !isLineBasedAnnotation(annotation)) {
      return annotation
    }

    return {
      ...annotation,
      geometry: {
        ...annotation.geometry,
        points: lineResizePoints,
        width: Math.abs(
          (lineResizePoints[2] ?? 0) - (lineResizePoints[0] ?? 0),
        ),
        height: Math.abs(
          (lineResizePoints[3] ?? 0) - (lineResizePoints[1] ?? 0),
        ),
      },
    }
  }, [annotation, lineResizePoints])

  const bounds = useMemo(() => {
    return {
      width: displayAnnotation.geometry.width ?? 0,
      height: displayAnnotation.geometry.height ?? 0,
    }
  }, [displayAnnotation.geometry])
  const localVisualRect = useMemo(
    () =>
      getLocalAnnotationVisualRect({
        annotation: displayAnnotation,
        baseSize: bounds,
      }),
    [displayAnnotation, bounds],
  )
  const selectionPadding = annotation.kind === 'icon' ? 2 : 6

  useEffect(() => {
    onNodeRef?.(template.id as string, groupRef.current)

    return () => {
      onNodeRef?.(template.id as string, null)
    }
  }, [onNodeRef, template.id])

  const textPosition = getTextPosition({
    annotation: displayAnnotation,
    width: bounds.width,
    height: bounds.height,
  })

  const arrowTextLayout = useMemo(
    () => getArrowTextLayout(displayAnnotation),
    [displayAnnotation],
  )
  const middleTextArrowLayout =
    arrowTextLayout?.placement === 'middle' ? arrowTextLayout : null
  const leftTextArrowLayout =
    arrowTextLayout?.placement === 'bottom' ? arrowTextLayout : null
  const arrowTextValue = arrowTextLayout?.text || 'Add text...'
  const isArrowTextPlaceholder = !arrowTextLayout?.text
  const arrowTextBoxPosition = arrowTextLayout
    ? {
        x: Math.round(x + arrowTextLayout.textX) - x,
        y: Math.round(y + arrowTextLayout.textY - 4) - y,
      }
    : null

  const bubbleLayout = useMemo(
    () => getBubbleLayout(displayAnnotation),
    [displayAnnotation],
  )

  const selectionFrame = useMemo(
    () =>
      getSelectionFrame({
        annotation: displayAnnotation,
        bubbleRadius: bubbleLayout?.bubbleRadius,
        selectionPadding,
        strokeWidth,
        visualRect: localVisualRect,
      }),
    [
      bubbleLayout,
      displayAnnotation,
      localVisualRect,
      selectionPadding,
      strokeWidth,
    ],
  )
  const hasPointSelectionHitArea = isLineBasedAnnotation(displayAnnotation)
  const selectOnPointerDown = (event: MouseEvent | TouchEvent) => {
    if (
      event instanceof MouseEvent &&
      (event.shiftKey || event.ctrlKey || event.metaKey)
    ) {
      return
    }

    onSelect?.()
  }
  const clearPendingKonvaDrag = () => {
    const group = groupRef.current
    if (!group || group.isDragging?.()) return

    group.draggable(false)
    group.draggable(draggable)
  }

  return (
    <Group
      ref={groupRef}
      id={template.id as string}
      x={x + (dragOffset?.x ?? 0)}
      y={y + (dragOffset?.y ?? 0)}
      rotation={rotation}
      opacity={opacity}
      draggable={draggable}
      listening={draggable || Boolean(onSelect)}
      onMouseDown={(event) => {
        markKonvaAnnotationPointerDown()
        event.cancelBubble = true
        event.evt.stopPropagation()
        selectOnPointerDown(event.evt)
        onPointerDown?.(event.evt)
        changeCursorType(event, 'grabbing')
      }}
      onTouchStart={(event) => {
        markKonvaAnnotationPointerDown()
        event.cancelBubble = true
        event.evt.stopPropagation()
        selectOnPointerDown(event.evt)
        onPointerDown?.(event.evt)
        changeCursorType(event, 'grabbing')
      }}
      onMouseUp={(event) => {
        clearPendingKonvaDrag()
        changeCursorType(event, 'grab')
        onPointerUp?.(event.evt)
      }}
      onTouchEnd={(event) => {
        clearPendingKonvaDrag()
        onPointerUp?.(event.evt)
      }}
      onClick={(event) => {
        clearPendingKonvaDrag()
        event.cancelBubble = true
        event.evt.stopPropagation()
      }}
      onTap={(event) => {
        clearPendingKonvaDrag()
        event.cancelBubble = true
        event.evt.stopPropagation()
      }}
      onContextMenu={(event) => {
        event.cancelBubble = true
        event.evt.preventDefault()
        event.evt.stopPropagation()
        onContextMenu?.(event)
      }}
      onDragStart={(event) => {
        event.cancelBubble = true
        onDragStart?.({
          x: event.target.x(),
          y: event.target.y(),
        })
      }}
      onDragMove={(event) => {
        event.cancelBubble = true
        onDragMove?.({
          x: event.target.x(),
          y: event.target.y(),
        })
      }}
      onDragEnd={(event) => {
        event.cancelBubble = true
        onDragEnd?.({
          x: event.target.x(),
          y: event.target.y(),
        })
      }}
      onMouseEnter={(event) => {
        changeCursorType(event, 'grab')
        onMouseEnter?.()
      }}
      onMouseLeave={(event) => {
        changeCursorType(event, 'default')
        onMouseLeave?.()
      }}
    >
      {hasPointSelectionHitArea &&
        (selectionFrame.mode === 'axis' ? (
          <Rect
            x={selectionFrame.rect.x}
            y={selectionFrame.rect.y}
            width={selectionFrame.rect.width}
            height={selectionFrame.rect.height}
            fill="rgba(0, 0, 0, 0.001)"
          />
        ) : (
          <SelectionFrameContent frame={selectionFrame}>
            <Rect
              x={selectionFrame.rect.x}
              y={selectionFrame.rect.y}
              width={selectionFrame.rect.width}
              height={selectionFrame.rect.height}
              fill="rgba(0, 0, 0, 0.001)"
            />
          </SelectionFrameContent>
        ))}
      {isSelected && (
        <SelectionFrameContent frame={selectionFrame}>
          <Rect
            x={selectionFrame.rect.x}
            y={selectionFrame.rect.y}
            width={selectionFrame.rect.width}
            height={selectionFrame.rect.height}
            stroke="#d2e8ff"
            strokeWidth={2}
            dash={[4, 3]}
            cornerRadius={selectionFrame.cornerRadius}
            listening={false}
          />
        </SelectionFrameContent>
      )}
      {(displayAnnotation.kind === 'rect' ||
        displayAnnotation.kind === 'roundRect' ||
        displayAnnotation.kind === 'circle' ||
        displayAnnotation.kind === 'ellipse' ||
        displayAnnotation.kind === 'triangle' ||
        displayAnnotation.kind === 'diamond') && (
        <KonvaAnnotationBoundsShape
          kind={displayAnnotation.kind}
          width={bounds.width}
          height={bounds.height}
          roundRectCornerRadius={
            displayAnnotation.geometry.mode === 'bounds'
              ? displayAnnotation.geometry.cornerRadius
              : undefined
          }
          isLegacySquare={isLegacySquare}
          isLegacyTriangle={isLegacyTriangle}
          stroke={stroke}
          strokeWidth={strokeWidth}
          fill={fill}
        />
      )}
      {displayAnnotation.kind === 'line' &&
        displayAnnotation.geometry.mode === 'points' && (
          <Line
            points={displayAnnotation.geometry.points}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
      {displayAnnotation.kind === 'arrow' &&
        displayAnnotation.geometry.mode === 'points' &&
        !middleTextArrowLayout &&
        !leftTextArrowLayout && (
          <Arrow
            points={displayAnnotation.geometry.points}
            stroke={stroke}
            fill={stroke}
            strokeWidth={strokeWidth}
            pointerLength={10}
            pointerWidth={10}
          />
        )}
      {leftTextArrowLayout && (
        <Arrow
          points={leftTextArrowLayout.arrowPoints}
          stroke={stroke}
          fill={stroke}
          strokeWidth={strokeWidth}
          pointerLength={10}
          pointerWidth={10}
        />
      )}
      {middleTextArrowLayout && (
        <Arrow
          points={middleTextArrowLayout.arrowPoints}
          stroke={stroke}
          fill={stroke}
          strokeWidth={strokeWidth}
          pointerLength={10}
          pointerWidth={10}
          pointerAtBeginning
        />
      )}
      {renderArrowText && arrowTextLayout && arrowTextBoxPosition && (
        <>
          <Rect
            x={arrowTextBoxPosition.x}
            y={arrowTextBoxPosition.y}
            width={arrowTextLayout.textWidth}
            height={arrowTextLayout.textHeight}
            fill="#ffffff"
            cornerRadius={arrowTextLayout.textCornerRadius}
          />
          <Text
            x={arrowTextBoxPosition.x + 12}
            y={arrowTextBoxPosition.y}
            width={Math.max(arrowTextLayout.textWidth - 24, 1)}
            height={arrowTextLayout.textHeight}
            text={arrowTextValue}
            fill={
              isArrowTextPlaceholder
                ? '#64748b'
                : (displayAnnotation.text?.color ?? '#000000')
            }
            fontSize={arrowTextLayout.fontSize}
            fontFamily={
              displayAnnotation.text?.fontFamily ?? DEFAULT_TEXT_FONT_FAMILY
            }
            align={displayAnnotation.text?.align ?? 'center'}
            verticalAlign={displayAnnotation.text?.verticalAlign ?? 'middle'}
          />
        </>
      )}
      {displayAnnotation.kind === 'bubble' &&
        displayAnnotation.geometry.mode === 'points' && (
          <>
            <Circle
              x={bubbleLayout?.bubbleCenter.x ?? 0}
              y={bubbleLayout?.bubbleCenter.y ?? 0}
              radius={bubbleLayout?.bubbleRadius ?? 10}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
            <Line
              points={
                bubbleLayout?.linePoints ?? displayAnnotation.geometry.points
              }
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
            <Arrow
              points={
                bubbleLayout?.linePoints ?? displayAnnotation.geometry.points
              }
              stroke={stroke}
              fill={stroke}
              strokeWidth={strokeWidth}
              pointerLength={10}
              pointerWidth={10}
            />
            {displayAnnotation.bubbleValue && (
              <Text
                x={
                  (bubbleLayout?.bubbleCenter.x ?? 0) -
                  (bubbleLayout?.bubbleRadius ?? 10)
                }
                y={(bubbleLayout?.bubbleCenter.y ?? 0) - 6}
                width={(bubbleLayout?.bubbleRadius ?? 10) * 2}
                text={displayAnnotation.bubbleValue}
                fill="#000000"
                fontSize={12}
                align="center"
              />
            )}
          </>
        )}
      {displayAnnotation.kind === 'icon' && (
        <>
          <Rect
            width={Math.max(bounds.width, 1)}
            height={Math.max(bounds.height, 1)}
            fill="rgba(0, 0, 0, 0.001)"
          />
          {annotation.iconName === 'checkmark' ? (
            <HardwareCheckmarkIcon
              width={Math.max(bounds.width, 1)}
              height={Math.max(bounds.height, 1)}
              color={stroke}
            />
          ) : annotation.iconSrc ? (
            <HardwareIconImage
              src={annotation.iconSrc}
              width={Math.max(bounds.width, 1)}
              boxHeight={Math.max(bounds.height, 1)}
            />
          ) : (
            <>
              <Rect
                width={Math.max(bounds.width, 28)}
                height={Math.max(bounds.height, 28)}
                stroke={stroke}
                strokeWidth={strokeWidth}
                dash={[4, 4]}
                cornerRadius={6}
              />
              <Text
                x={4}
                y={Math.max(bounds.height, 28) / 2 - 6}
                width={Math.max(bounds.width, 28) - 8}
                align="center"
                text={annotation.iconName ?? annotation.legacyType ?? 'icon'}
                fill={stroke}
                fontSize={10}
                ellipsis
              />
            </>
          )}
        </>
      )}
      {textPosition &&
        displayAnnotation.text?.value !== undefined &&
        !(
          displayAnnotation.kind === 'arrow' &&
          (displayAnnotation.text?.placement === 'middle' ||
            displayAnnotation.text?.placement === 'bottom')
        ) && (
          <Text
            x={textPosition.x}
            y={textPosition.y}
            width={textPosition.width}
            text={displayAnnotation.text.value}
            fill={displayAnnotation.text.color ?? stroke}
            fontSize={displayAnnotation.text.fontSize ?? 14}
            align={displayAnnotation.text.align ?? 'center'}
          />
        )}
    </Group>
  )
}

export const KonvaAnnotationNode = memo(KonvaAnnotationNodeComponent)
KonvaAnnotationNode.displayName = 'KonvaAnnotationNode'

export default KonvaAnnotationNode
